import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Power, KeyRound, Users as UsersIcon, Search } from "lucide-react";
import { MBTI_GROUPS, MbtiBadge } from "@/lib/mbti";
import { VIA_STRENGTHS, VIA_ALL } from "@/lib/via";
import CsvImporter from "@/components/CsvImporter";
import SearchableSelect from "@/components/SearchableSelect";
import PhotoUpload, { Avatar } from "@/components/PhotoUpload";

const emptyForm = { name: "", email: "", phone: "", team_id: "", recruited_by: "", recruited_by_coach_user_id: "", recommendation_type: "", mbti: "", mission: "", vision: "", principles: "", via_top5: ["", "", "", "", ""], joined_at: "", league_status: "competing", role_in_team: "consultant", is_team_lead: false };

export default function PlayersAdmin({ defaultRole = null, pageTitle = null }) {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const formRef = useRef(null);
  const [form, setForm] = useState({ ...emptyForm, role_in_team: defaultRole || "consultant" });
  // Search & filters (com 240+ consultores torna-se essencial)
  const [search, setSearch] = useState("");
  const [filterTeam, setFilterTeam] = useState("");
  const [filterClub, setFilterClub] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // "" | "active" | "inactive" | "expelled" | "bench"
  const [filterRole, setFilterRole] = useState(defaultRole || ""); // "" | "consultant" | "assistant" | "team_lead"

  const refresh = () => api.get("/players").then((r) => setPlayers(r.data));

  // Memoize the recruiter picker list — recomputed only when players or editing change,
  // not on every keystroke in the form (which would re-filter ~100 players × N inputs).
  const recruiterOptions = useMemo(
    () => players.filter((p) => p.id !== editing),
    [players, editing],
  );

  // Lista filtrada após pesquisa + filtros (case/acento-insensível)
  const visiblePlayers = useMemo(() => {
    const norm = (s) => (s || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const q = norm(search.trim());
    return players.filter((p) => {
      if (q) {
        const hay = [p.name, p.email, p.phone, p.team_name, p.club, p.mbti].map(norm).join(" ");
        if (!hay.includes(q)) return false;
      }
      if (filterTeam && p.team_id !== filterTeam) return false;
      if (filterClub && p.club !== filterClub) return false;
      if (filterStatus === "active" && !(p.is_active && !p.expelled)) return false;
      if (filterStatus === "inactive" && p.is_active) return false;
      if (filterStatus === "expelled" && !p.expelled) return false;
      if (filterStatus === "bench" && !["bench", "observer", "substitute"].includes(p.league_status)) return false;
      if (filterRole === "consultant" && (p.role_in_team || "consultant") !== "consultant") return false;
      if (filterRole === "assistant" && p.role_in_team !== "assistant") return false;
      if (filterRole === "team_lead" && !p.is_team_lead) return false;
      return true;
    });
  }, [players, search, filterTeam, filterClub, filterStatus, filterRole]);

  const clubsList = useMemo(() => {
    const set = new Set(players.map((p) => p.club).filter(Boolean));
    return Array.from(set).sort();
  }, [players]);

  useEffect(() => {
    refresh();
    api.get("/teams").then((r) => setTeams(r.data));
    api.get("/users").then((r) => {
      setCoaches((r.data || []).filter((u) => u.role === "coach"));
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        team_id: form.team_id || null,
        recruited_by: form.recruited_by || null,
        recruited_by_coach_user_id: form.recruited_by_coach_user_id || null,
        recommendation_type: form.recommendation_type || null,
        mbti: form.mbti || null,
        mission: form.mission || null,
        vision: form.vision || null,
        principles: form.principles || null,
        via_top5: (form.via_top5 || []).filter(Boolean),
        joined_at: form.joined_at || null,
        league_status: form.league_status || "competing",
        role_in_team: form.role_in_team || "consultant",
        is_team_lead: !!form.is_team_lead,
      };
      if (editing) {
        await api.put(`/players/${editing}`, payload);
        toast.success("Consultor atualizado");
      } else {
        await api.post("/players", payload);
        toast.success("Consultor criado");
      }
      setForm(emptyForm); setEditing(null); setShowForm(false); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const edit = (p) => {
    setEditing(p.id);
    setShowForm(true);
    setForm({
      name: p.name,
      email: p.email || "",
      phone: p.phone || "",
      team_id: p.team_id || "",
      recruited_by: p.recruited_by || "",
      recruited_by_coach_user_id: p.recruited_by_coach_user_id || "",
      recommendation_type: p.recommendation_type || "",
      mbti: p.mbti || "",
      mission: p.mission || "",
      vision: p.vision || "",
      principles: p.principles || "",
      via_top5: [...(p.via_top5 || []), "", "", "", "", ""].slice(0, 5),
      joined_at: p.joined_at || "",
      league_status: p.league_status || "competing",
      role_in_team: p.role_in_team || "consultant",
      is_team_lead: !!p.is_team_lead,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const del = async (id) => {
    if (!window.confirm("Eliminar consultor e respetivos registos?")) return;
    try {
      await api.delete(`/players/${id}`);
      toast.success("Consultor eliminado");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/players/${p.id}`, {
        name: p.name, email: p.email, team_id: p.team_id, recruited_by: p.recruited_by,
        mbti: p.mbti, mission: p.mission, via_top5: p.via_top5,
        is_active: !p.is_active,
      });
      toast.success(`Consultor ${!p.is_active ? "ativado" : "inativado"}`);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const createLogin = async (p) => {
    if (!p.email) { toast.error("Este consultor não tem email — adiciona um primeiro."); return; }
    if (!window.confirm(`Criar conta de login para ${p.name} e enviar email "Define palavra-passe" para ${p.email}?`)) return;
    try {
      const r = await api.post(`/players/${p.id}/create-login`);
      if (r.data.created) toast.success(`Conta criada · email ${r.data.email_sent ? "enviado" : "não enviado (Resend?)"}`);
      else toast.info(`Já existia conta para este email · vínculo atualizado`);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const provisionAll = async () => {
    const missing = players.filter((p) => !p.user_id && p.email).length;
    if (missing === 0) { toast.info("Todos os consultores já têm conta de login."); return; }
    if (!window.confirm(`Provisionar contas para ${missing} consultor(es) e enviar email "Define palavra-passe" a cada um?`)) return;
    try {
      const r = await api.post(`/admin/provision-all-logins`);
      toast.success(`${r.data.created} conta(s) criada(s) · ${r.data.skipped} já existiam · ${r.data.no_email} sem email`);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8 kw-fade-up" data-testid="players-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kw-overline mb-2">Administração</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            {pageTitle || "Consultores"}
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-2" data-testid="players-count">
            {players.length} {defaultRole === "assistant" ? "assistente(s)" : "consultor(es)"} na base
          </p>
          {defaultRole === "assistant" && (
            <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
              Gestão exclusiva das assistentes da KW Lead. As assistentes têm ranking separado e não coopetem com os consultores.
            </p>
          )}
        </div>
        <button
          onClick={() => { if (showForm) { setEditing(null); setForm(emptyForm); } setShowForm(!showForm); }}
          className="kw-btn-primary inline-flex items-center gap-2"
          data-testid="toggle-player-form">
          {showForm ? <><X className="w-4 h-4" /> Fechar formulário</> : <><Plus className="w-4 h-4" /> Adicionar {defaultRole === "assistant" ? "assistente" : "consultor"}</>}
        </button>
      </div>

      <div ref={formRef} />
      {showForm && (
      <form onSubmit={submit} className="kw-card p-6 grid md:grid-cols-3 lg:grid-cols-6 gap-4 items-end" data-testid="player-form">
        <Field label="Nome">
          <input className="kw-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="player-name" />
        </Field>
        <Field label="Email (opcional)">
          <input type="email" className="kw-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="player-email" />
        </Field>
        <Field label="Telemóvel (opcional)">
          <input className="kw-input" placeholder="+351 ..." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="player-phone" />
        </Field>
        <Field label="Equipa KW">
          <SearchableSelect
            options={teams.map((t) => ({ id: t.id, label: t.name, sublabel: t.team_lead ? `Team Lead: ${t.team_lead.name}` : null }))}
            value={form.team_id}
            onChange={(id) => setForm({ ...form, team_id: id })}
            placeholder="Escrever nome da equipa…"
            emptyOption="— sem equipa —"
            testid="player-team"
          />
        </Field>
        <Field label="Perfil MBTI">
          <select className="kw-input" value={form.mbti} onChange={(e) => setForm({ ...form, mbti: e.target.value })} data-testid="player-mbti">
            <option value="">— sem perfil —</option>
            {MBTI_GROUPS.map((g) => (
              <optgroup key={g.club} label={g.club}>
                {g.types.map((t) => (
                  <option key={t.code} value={t.code}>{t.code} · {t.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </Field>
        <Field label="Recrutado por">
          <SearchableSelect
            options={recruiterOptions.map((p) => ({
              id: p.id,
              label: p.name,
              sublabel: [p.club, p.mbti, p.team_name].filter(Boolean).join(" · ") || null,
            }))}
            value={form.recruited_by}
            onChange={(id) => setForm({ ...form, recruited_by: id })}
            placeholder="Escrever nome do recrutador…"
            emptyOption="— Sem recrutador —"
            testid="player-recruiter"
          />
        </Field>
        <Field label="Tipo de recomendação (auto Growth Share)">
          <select className="kw-input" value={form.recommendation_type}
            onChange={(e) => setForm({ ...form, recommendation_type: e.target.value })}
            data-testid="player-recommendation-type"
            disabled={!form.recruited_by}>
            <option value="">— Nenhum —</option>
            <option value="internal">Interno (+20 pts ao recrutador)</option>
            <option value="external">Externo (+100 pts ao recrutador)</option>
          </select>
        </Field>
        <Field label="Consultor recrutador (Growth Share)">
          <SearchableSelect
            options={coaches.map((c) => ({
              id: c.id,
              label: c.name || c.email,
              sublabel: c.club || null,
            }))}
            value={form.recruited_by_coach_user_id}
            onChange={(id) => setForm({ ...form, recruited_by_coach_user_id: id })}
            placeholder="Selecionar consultor…"
            emptyOption="— Auto (secretário do clube pelo MBTI) —"
            testid="player-recruited-by-coach"
          />
          <p className="text-[10px] text-[#737373] mt-1">
            Deixe vazio para atribuição automática ao secretário do clube (Growth Share Interno +20).
            Selecione manualmente para marcar como referral externo do coach (+100 se `recommendation_type=external`).
          </p>
        </Field>
        <Field label="Data de entrada na KW Lead">
          <input type="date" className="kw-input"
            value={form.joined_at || ""}
            onChange={(e) => setForm({ ...form, joined_at: e.target.value })}
            data-testid="player-joined-at" />
          <p className="text-[10px] text-[#737373] mt-1">
            Usada para email automático de aniversário. Se vazio, será usada a data de criação do registo.
          </p>
        </Field>
        <Field label="Estado na Liga (Growth League)">
          <select className="kw-input" value={form.league_status || "competing"}
            onChange={(e) => setForm({ ...form, league_status: e.target.value })}
            data-testid="player-league-status">
            <option value="competing">A coopetir — entra nos rankings e MVPs</option>
            <option value="observer">Observador — reporta e vê a sua accountability, fora do campeonato</option>
          </select>
          <p className="text-[10px] text-[#737373] mt-1">
            Os observadores têm o seu próprio Ranking Accountability. Quem entra depois
            do arranque da edição fica automaticamente como Observador.
          </p>
        </Field>
        <Field label="Papel na equipa">
          <select className="kw-input" value={form.role_in_team || "consultant"}
            onChange={(e) => setForm({ ...form, role_in_team: e.target.value, is_team_lead: e.target.value === "assistant" ? false : form.is_team_lead })}
            data-testid="player-role-in-team">
            <option value="consultant">Consultor — ranking principal</option>
            <option value="assistant">Assistente — ranking separado (sem angariações nem vendas)</option>
          </select>
        </Field>
        <Field label="Team Lead da equipa">
          <label className="flex items-center gap-2 text-sm h-[42px]">
            <input type="checkbox"
              checked={!!form.is_team_lead}
              disabled={form.role_in_team === "assistant"}
              onChange={(e) => setForm({ ...form, is_team_lead: e.target.checked })}
              data-testid="player-is-team-lead"
              className="w-4 h-4" />
            <span className={form.role_in_team === "assistant" ? "text-[#555]" : "text-[#A3A3A3]"}>
              Lidera a equipa (apenas 1 por equipa)
            </span>
          </label>
        </Field>
        <div className="flex gap-2">
          <button className="kw-btn-primary inline-flex items-center gap-2" data-testid="player-submit">
            <Plus className="w-4 h-4" /> {editing ? "Guardar" : "Adicionar"}
          </button>
          {editing && (
            <button type="button" className="kw-btn-secondary" onClick={() => { setEditing(null); setForm(emptyForm); setShowForm(false); }}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="md:col-span-3 lg:col-span-6 grid md:grid-cols-2 gap-4 pt-3 border-t border-[#222]">
          <div>
            <label className="kw-overline block mb-2">Missão pessoal do consultor</label>
            <textarea className="kw-input min-h-[80px]" placeholder="A minha missão é…"
              value={form.mission} onChange={(e) => setForm({ ...form, mission: e.target.value })}
              data-testid="player-mission" />
          </div>
          <div>
            <label className="kw-overline block mb-2">Visão pessoal</label>
            <textarea className="kw-input min-h-[80px]" placeholder="A minha visão é…"
              value={form.vision} onChange={(e) => setForm({ ...form, vision: e.target.value })}
              data-testid="player-vision" />
          </div>
          <div className="md:col-span-2">
            <label className="kw-overline block mb-2">Princípios pessoais</label>
            <textarea className="kw-input min-h-[80px]" placeholder="Os meus princípios..."
              value={form.principles} onChange={(e) => setForm({ ...form, principles: e.target.value })}
              data-testid="player-principles" />
          </div>
          <div className="md:col-span-2">
            <label className="kw-overline block mb-2">Top 5 Forças VIA (do teste VIA)</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[0,1,2,3,4].map((i) => {
                const selected = form.via_top5 || [];
                const others = selected.filter((_, idx) => idx !== i).filter(Boolean);
                return (
                  <select key={i} className="kw-input !py-2"
                    value={selected[i] || ""}
                    onChange={(e) => {
                      const v = [...selected]; v[i] = e.target.value; setForm({ ...form, via_top5: v });
                    }}
                    data-testid={`player-via-${i}`}>
                    <option value="">— Força #{i + 1} —</option>
                    {Object.entries(VIA_STRENGTHS).map(([virtude, items]) => (
                      <optgroup key={virtude} label={virtude}>
                        {items.map((s) => (
                          <option key={s} value={s} disabled={others.includes(s)}>{s}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                );
              })}
            </div>
          </div>
        </div>
      </form>
      )}

      <ClubLegend />

      <div className="flex flex-wrap items-center gap-3 kw-card p-3" data-testid="bulk-actions">
        <UsersIcon className="w-4 h-4 text-[#E50000]" />
        <div className="flex-1 min-w-0">
          <div className="kw-overline text-[10px]">Contas de Login</div>
          <p className="text-xs text-[#A3A3A3]">
            Cria contas de login para consultores que ainda não conseguem entrar — eles recebem um email para definir a própria palavra-passe.
          </p>
        </div>
        <button onClick={provisionAll} className="kw-btn-secondary inline-flex items-center gap-1.5 text-xs" data-testid="provision-all-logins">
          <KeyRound className="w-3 h-3" /> Provisionar todas em falta
        </button>
      </div>

      <CsvImporter
        endpoint="/import/players"
        templateEndpoint="/import/players/template"
        label="Importar membros KW Lead em massa (CSV ou Excel)"
        sampleHeaders="nome, email, telefone, KW UID, role, mbti, team, recruited_by, data entrada KW, Data/Hora Resposta MBTI, Fonte MBTI — quem já existe é atualizado"
        loginToggle
        onDone={refresh}
      />

      {/* Barra de pesquisa + filtros (essencial com 240+ consultores) */}
      <div className="kw-card p-4 flex flex-wrap items-end gap-3" data-testid="players-search-bar">
        <div className="flex-1 min-w-[220px]">
          <label className="kw-overline block mb-1 text-[10px]">Pesquisar</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
            <input
              type="text"
              className="kw-input pl-9"
              placeholder="Nome, email, telemóvel, equipa, clube, MBTI…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="players-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white"
                data-testid="players-search-clear"
                aria-label="Limpar pesquisa"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <div className="min-w-[140px]">
          <label className="kw-overline block mb-1 text-[10px]">Equipa</label>
          <select className="kw-input" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} data-testid="players-filter-team">
            <option value="">Todas</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="min-w-[140px]">
          <label className="kw-overline block mb-1 text-[10px]">Clube</label>
          <select className="kw-input" value={filterClub} onChange={(e) => setFilterClub(e.target.value)} data-testid="players-filter-club">
            <option value="">Todos</option>
            {clubsList.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="min-w-[140px]">
          <label className="kw-overline block mb-1 text-[10px]">Estado</label>
          <select className="kw-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="players-filter-status">
            <option value="">Todos</option>
            <option value="active">Ativo</option>
            <option value="inactive">Inativo</option>
            <option value="expelled">Excluído</option>
            <option value="bench">Bench</option>
          </select>
        </div>
        <div className="min-w-[150px]">
          <label className="kw-overline block mb-1 text-[10px]">Papel</label>
          <select className="kw-input" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} data-testid="players-filter-role">
            <option value="">Todos</option>
            <option value="consultant">Consultor</option>
            <option value="assistant">Assistente</option>
            <option value="team_lead">Team Lead</option>
          </select>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#737373] whitespace-nowrap pb-2.5">
          {visiblePlayers.length} / {players.length} consultor{players.length === 1 ? "" : "es"}
        </div>
      </div>

      <div className="kw-card overflow-x-auto hidden md:block">
        <table className="w-full text-left text-sm" data-testid="players-table">
          <thead className="kw-thead-sticky border-b border-[#222]">
            <tr className="kw-overline">
              <th className="p-3 w-12"></th>
              <th className="p-3">Nome</th>
              <th className="p-3">Email</th>
              <th className="p-3">Equipa</th>
              <th className="p-3">Perfil MBTI</th>
              <th className="p-3">Clube</th>
              <th className="p-3">Foto</th>
              <th className="p-3">Estado</th>
              <th className="p-3 text-right kw-th-sticky-col-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {visiblePlayers.length === 0 && <tr><td colSpan="9" className="p-6 text-center text-[#737373]">{players.length === 0 ? "Sem consultores." : "Nenhum consultor corresponde aos filtros."}</td></tr>}
            {visiblePlayers.map((p) => (
              <tr key={p.id} className="border-b border-[#1A1A1A] hover:bg-[#141414]">
                <td className="p-3"><Avatar photoUrl={p.photo_url} name={p.name} size={40} /></td>
                <td className="p-3 font-semibold">{p.name}</td>
                <td className="p-3 text-[#A3A3A3]">{p.email || "—"}</td>
                <td className="p-3">{p.team_name || "—"}</td>
                <td className="p-3"><MbtiBadge mbti={p.mbti} label={p.personality_label} /></td>
                <td className="p-3">{p.club ? <MbtiBadge club={p.club} /> : <span className="text-[#737373] text-xs">—</span>}</td>
                <td className="p-3"><PhotoUpload playerId={p.id} photoUrl={p.photo_url} name={p.name} size="sm" onChange={refresh} /></td>
                <td className="p-3">
                  {p.expelled ? <span className="text-[#FF6B6B] text-xs font-bold uppercase tracking-widest">Excluído</span> :
                    p.is_active ? <span className="text-[#00FF88] text-xs font-bold uppercase tracking-widest">Ativo</span> :
                    <span className="text-[#FFB020] text-xs font-bold uppercase tracking-widest">Inativo</span>}
                </td>
                <td className="p-3 text-right whitespace-nowrap kw-col-sticky-right">
                  <div className="inline-flex items-center gap-1">
                    {!p.user_id && p.email && (
                      <button
                        onClick={() => createLogin(p)}
                        className="w-8 h-8 inline-flex items-center justify-center border border-[#FFB020]/40 text-[#FFB020] hover:bg-[#FFB020]/10 hover:border-[#FFB020] transition-colors"
                        title="Criar login e enviar email"
                        aria-label="Criar login"
                        data-testid={`create-login-${p.id}`}
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => toggleActive(p)}
                      className={`w-8 h-8 inline-flex items-center justify-center border transition-colors ${p.is_active ? "border-[#00FF88]/40 text-[#00FF88] hover:bg-[#00FF88]/10 hover:border-[#00FF88]" : "border-[#FFB020]/40 text-[#FFB020] hover:bg-[#FFB020]/10 hover:border-[#FFB020]"}`}
                      title={p.is_active ? "Inativar consultor" : "Ativar consultor"}
                      aria-label={p.is_active ? "Inativar" : "Ativar"}
                      data-testid={`toggle-${p.id}`}
                    >
                      <Power className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => edit(p)}
                      className="w-8 h-8 inline-flex items-center justify-center border border-[#333] hover:border-white hover:bg-white/5 transition-colors"
                      title="Editar consultor"
                      aria-label="Editar"
                      data-testid={`edit-${p.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => del(p.id)}
                      className="w-8 h-8 inline-flex items-center justify-center border border-[#FF6B6B]/40 text-[#FF6B6B] hover:bg-[#FF6B6B]/10 hover:border-[#FF6B6B] transition-colors"
                      title="Eliminar consultor"
                      aria-label="Eliminar"
                      data-testid={`del-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden grid gap-3" data-testid="players-cards">
        {visiblePlayers.length === 0 && <div className="kw-card p-6 text-center text-[#737373]">{players.length === 0 ? "Sem consultores." : "Nenhum consultor corresponde aos filtros."}</div>}
        {visiblePlayers.map((p) => (
          <div key={p.id} className="kw-card p-4">
            <div className="flex items-center gap-3 mb-3">
              <Avatar photoUrl={p.photo_url} name={p.name} size={48} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{p.name}</div>
                <div className="text-xs text-[#A3A3A3] truncate">{p.email || "—"}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">{p.team_name || "Sem equipa"}</div>
              </div>
              <div>
                {p.expelled ? <span className="text-[#FF6B6B] text-[10px] font-bold uppercase tracking-widest">Excluído</span> :
                  p.is_active ? <span className="text-[#00FF88] text-[10px] font-bold uppercase tracking-widest">Ativo</span> :
                  <span className="text-[#FFB020] text-[10px] font-bold uppercase tracking-widest">Inativo</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {p.mbti && <MbtiBadge mbti={p.mbti} label={p.personality_label} compact />}
              {p.club && <MbtiBadge club={p.club} />}
            </div>
            <div className="flex flex-wrap gap-2 pt-3 border-t border-[#222]">
              <PhotoUpload playerId={p.id} photoUrl={p.photo_url} name={p.name} size="sm" onChange={refresh} />
              <button onClick={() => toggleActive(p)} className="kw-btn-secondary !py-1 !px-3 inline-flex items-center gap-1 text-xs" data-testid={`toggle-m-${p.id}`}>
                <Power className="w-3 h-3" /> {p.is_active ? "Inativar" : "Ativar"}
              </button>
              <button onClick={() => edit(p)} className="kw-btn-secondary !py-1 !px-3 inline-flex items-center gap-1 text-xs" data-testid={`edit-m-${p.id}`}>
                <Pencil className="w-3 h-3" /> Editar
              </button>
              <button onClick={() => del(p.id)} className="text-[#FF6B6B] inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold ml-auto" data-testid={`del-m-${p.id}`}>
                <Trash2 className="w-3 h-3" /> Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="kw-overline block mb-2">{label}</label>
      {children}
    </div>
  );
}

function ClubLegend() {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
      {MBTI_GROUPS.map((g) => (
        <div key={g.club} className="kw-card p-4" style={{ borderColor: g.border }}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-display text-lg font-black uppercase tracking-tight" style={{ color: g.color }}>{g.club}</div>
            <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: g.color }}>
              {g.types.map((t) => t.code).join(" · ")}
            </span>
          </div>
          <p className="text-[11px] text-[#A3A3A3] leading-snug">{g.description}</p>
        </div>
      ))}
    </div>
  );
}
